import {
  type MouseEvent,
  type PropsWithChildren,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { withRtlSupport } from '@skyscanner-internal/backpack-web/bpk-component-icon';
import LongArrowRight from '@skyscanner-internal/backpack-web/bpk-component-icon/sm/long-arrow-right';
import BpkInfoBanner from '@skyscanner-internal/backpack-web/bpk-component-info-banner';
import BpkLink from '@skyscanner-internal/backpack-web/bpk-component-link';
import BpkText, {
  TEXT_STYLES,
} from '@skyscanner-internal/backpack-web/bpk-component-text';
import classNames from 'classnames';
import reactReplacePlaceholder from 'react-replace-placeholder';

import FlightStatusBadge from '../FlightStatusBadge';
import {
  getDisplayFlightCode,
  getScheduledArrivalAirportCode,
  getScheduledArrivalAirportName,
  isDivertedFlight,
} from '../flightStatusUpdate';
import {
  formatDisplayDate,
  formatMinuteDelay,
  getDelay,
  getScheduledOnly,
  getStatusLabel,
  resolveTime,
  shouldUseDivertedStatus,
} from '../utils';

import type { ResolvedTime } from '../utils';
import type { NetSkyscannerArrivaldeparturesvcV1ArrivalDepartureService as ArrivalDepartureTypes } from '@skyscanner-internal/arrival-departure-svc-web-http-client';
import type { FlightStatusDetailsProps } from '@skyscanner-internal/landing-pages-shared-types/types-shared/FlightStatusDetailsProps';

import STYLES from './FlightStatusHeader.module.scss';

type FlightStatusUpdate = ArrivalDepartureTypes.FlightStatusUpdate;

export type FlightStatusHeaderProps = {
  title: string;
  carrierCode: string;
  selectedFlight: FlightStatusUpdate | null;
  strings: FlightStatusDetailsProps['strings'];
  locale: string;
};

const RtlLongArrowRight = withRtlSupport(LongArrowRight);

/**
 * Refresh the page to fetch fresh flight data.
 * Called from refresh link in the Status section.
 */
const handleRefresh = () => {
  if (typeof globalThis.window === 'undefined') {
    return;
  }
  globalThis.window.location.reload();
};

const formatRouteCode = (departureCode?: string, arrivalCode?: string) => {
  const ariaLabel = `Route ${departureCode} to ${arrivalCode}`;

  return (
    <span
      className={STYLES.FlightStatusHeader__routeCode}
      aria-label={ariaLabel}
    >
      <span>{departureCode}</span>
      <RtlLongArrowRight aria-hidden="true" focusable="false" />
      <span>{arrivalCode}</span>
    </span>
  );
};

// Departure delay for the status-badge "(N minutes)" annotation and the
// SCHEDULED->DELAYED promotion check — same calculation the subtitle uses.
const getDepartureDelayMinutes = (
  flight: FlightStatusUpdate,
  locale: string,
): number | null => {
  const delay = getDelay(
    flight,
    'departure',
    resolveTime(flight, 'departure', locale),
  );
  return delay.bucket === 'delayed' ? delay.minutes : null;
};

type SummaryPlaceholderValue = ResolvedTime | string | null;

// If any placeholder the chosen template needs didn't resolve, suppress
// the whole sentence (return null) rather than render a partial one.
const assembleSummary = (
  stringKey: keyof FlightStatusDetailsProps['strings'],
  strings: FlightStatusDetailsProps['strings'],
  placeholders: Record<string, SummaryPlaceholderValue>,
): ReturnType<typeof reactReplacePlaceholder> | null => {
  const resolved: Record<string, string> = {};
  for (const [key, value] of Object.entries(placeholders)) {
    const formatted = typeof value === 'string' ? value : value?.formatted;
    if (formatted === null || formatted === undefined) {
      return null;
    }
    resolved[key] = formatted;
  }
  return reactReplacePlaceholder(strings[stringKey], resolved, '@@', '@@');
};

type Phase = 'not-departed' | 'departed' | 'landed' | 'diverted' | 'cancelled';

// `departureActual` must be actual-only, not the full cascade — otherwise
// a flight with just an ETA would be misclassified as 'departed'.
const getPhase = (
  flight: FlightStatusUpdate,
  departureActual: ResolvedTime,
): Phase => {
  if (flight.status === 'FLIGHT_STATUS_CANCELLED') return 'cancelled';
  if (isDivertedFlight(flight)) return 'diverted';
  if (
    [
      'FLIGHT_STATUS_LANDED',
      'FLIGHT_STATUS_IN_GATE',
      'FLIGHT_STATUS_PAST_FLIGHT',
    ].includes(flight.status ?? '')
  ) {
    return 'landed';
  }
  if (
    ['FLIGHT_STATUS_IN_AIR', 'FLIGHT_STATUS_OUT_GATE'].includes(
      flight.status ?? '',
    ) ||
    departureActual.source !== 'unavailable'
  ) {
    return 'departed';
  }
  return 'not-departed';
};

const getFlightSummary = (
  flight: FlightStatusUpdate | null,
  locale: string,
  carrierCode: string,
  strings: FlightStatusDetailsProps['strings'],
) => {
  if (!flight) {
    return null;
  }

  if (flight.status === 'FLIGHT_STATUS_CANCELLED') {
    return null;
  }

  const departureAirport =
    flight.departureAirportName ||
    flight.departureAirportCode ||
    strings.FlightTrackerFlight_status_not_available;
  const scheduledArrivalAirport =
    getScheduledArrivalAirportName(flight) ||
    getScheduledArrivalAirportCode(flight) ||
    strings.FlightTrackerFlight_status_not_available;
  const actualArrivalAirport =
    flight.arrivalAirportName ||
    flight.arrivalAirportCode ||
    strings.FlightTrackerFlight_status_not_available;

  const flightNumber = getDisplayFlightCode(flight, carrierCode);

  const departure = resolveTime(flight, 'departure', locale);
  const arrival = resolveTime(flight, 'arrival', locale);
  const scheduledDepartureOnly = getScheduledOnly(flight, 'departure', locale);
  const scheduledArrivalOnly = getScheduledOnly(flight, 'arrival', locale);
  const departureActualOnly = resolveTime(flight, 'departure', locale, [
    'actual',
  ]);

  const departureDelay = getDelay(flight, 'departure', departure);
  const arrivalDelay = getDelay(flight, 'arrival', arrival);

  const phase = getPhase(flight, departureActualOnly);

  // departure_date must match whichever departure time field is actually
  // shown alongside it (scheduled vs resolved), not always the scheduled
  // time.
  const departureDateFromSchedule = formatDisplayDate(
    scheduledDepartureOnly.raw ?? undefined,
    locale,
    null,
  );
  const departureDateFromResolved = formatDisplayDate(
    departure.raw ?? undefined,
    locale,
    null,
  );

  const commonBase = {
    airline:
      flight.airlineName || strings.FlightTrackerFlight_status_not_available,
    flight_number: flightNumber,
    departure_airport: departureAirport,
    arrival_airport: scheduledArrivalAirport,
  };

  if (phase === 'diverted') {
    const airportKnown =
      Boolean(flight.arrivalAirportCode) &&
      Boolean(flight.scheduledArrivalAirportCode) &&
      flight.arrivalAirportCode !== flight.scheduledArrivalAirportCode;

    // No actual departure time at all — use "scheduled to depart" framing
    // instead of "left...". Checks actual-only, not the full cascade, so
    // the branch can still trigger when a schedule/estimate is known.
    if (departureActualOnly.source === 'unavailable') {
      // `arrival_airport` isn't used by the `_unknown` copy — built via
      // property access (not destructuring) to avoid binding a snake_case
      // identifier. The `_known` variant still gets the full `commonBase`.
      const commonBaseWithoutArrival = {
        airline: commonBase.airline,
        flight_number: commonBase.flight_number,
        departure_airport: commonBase.departure_airport,
      };

      return assembleSummary(
        airportKnown
          ? 'FlightTrackerFlight_body_diverted_airport_known_no_departure_time'
          : 'FlightTrackerFlight_body_diverted_airport_unknown_no_departure_time',
        strings,
        {
          ...(airportKnown ? commonBase : commonBaseWithoutArrival),
          departure_date: departureDateFromSchedule,
          scheduled_departure_time: scheduledDepartureOnly.formatted,
          ...(airportKnown ? { diverted_airport: actualArrivalAirport } : {}),
        },
      );
    }

    return assembleSummary(
      airportKnown
        ? 'FlightTrackerFlight_body_diverted_airport_known'
        : 'FlightTrackerFlight_body_diverted_airport_unknown',
      strings,
      {
        ...commonBase,
        departure_date: departureDateFromResolved,
        actual_departure_time: departure.formatted,
        ...(airportKnown ? { diverted_airport: actualArrivalAirport } : {}),
      },
    );
  }

  if (phase === 'landed') {
    // No actual or estimated time for either leg — suppress rather than
    // show the schedule under "left.../arrived..." wording.
    if (departure.source === 'scheduled' || arrival.source === 'scheduled') {
      return null;
    }

    // Keep `early_landed` for a flight that departed early and landed
    // without a calculable arrival delay.
    if (
      departureDelay.bucket === 'early' &&
      arrivalDelay.bucket !== 'delayed'
    ) {
      return assembleSummary('FlightTrackerFlight_body_early_landed', strings, {
        ...commonBase,
        departure_date: departureDateFromResolved,
        actual_departure_time: departure.formatted,
        actual_arrival_time: arrival.formatted,
      });
    }

    if (arrivalDelay.bucket === 'delayed') {
      return assembleSummary(
        'FlightTrackerFlight_body_landed_delayed',
        strings,
        {
          ...commonBase,
          departure_date: departureDateFromResolved,
          actual_departure_time: departure.formatted,
          actual_arrival_time: arrival.formatted,
          time_delayed: formatMinuteDelay(
            arrivalDelay.minutes as number,
            locale,
            strings,
          ),
        },
      );
    }

    return assembleSummary('FlightTrackerFlight_body_landed_on_time', strings, {
      ...commonBase,
      departure_date: departureDateFromResolved,
      actual_departure_time: departure.formatted,
      actual_arrival_time: arrival.formatted,
    });
  }

  if (phase === 'departed') {
    // No actual or estimated departure time — suppress rather than show
    // the schedule under "left..." wording.
    if (departure.source === 'scheduled') {
      return null;
    }

    // Arrival bucket is checked first, not departure-timing, so "departed
    // early, then delayed" always surfaces the delay rather than hiding it
    // behind "departed early" phrasing — this ordering must not change.
    if (arrivalDelay.bucket === 'delayed') {
      return assembleSummary(
        'FlightTrackerFlight_body_departed_delayed',
        strings,
        {
          ...commonBase,
          departure_date: departureDateFromResolved,
          actual_departure_time: departure.formatted,
          estimated_arrival_time: arrival.formatted,
          time_delayed: formatMinuteDelay(
            arrivalDelay.minutes as number,
            locale,
            strings,
          ),
        },
      );
    }

    // Departed early and not (yet) delayed on arrival — restore the
    // pre-existing "left...earlier than scheduled" phrasing, reusing the
    // existing `early_not_landed`/`_estimated` strings (still fully
    // translated, just orphaned by the arrival-bucket-only rewrite above).
    if (departureDelay.bucket === 'early') {
      if (arrival.source === 'scheduled') {
        return assembleSummary(
          'FlightTrackerFlight_body_early_not_landed',
          strings,
          {
            ...commonBase,
            departure_date: departureDateFromResolved,
            actual_departure_time: departure.formatted,
            scheduled_arrival_time: arrival.formatted,
          },
        );
      }

      return assembleSummary(
        'FlightTrackerFlight_body_early_not_landed_estimated',
        strings,
        {
          ...commonBase,
          departure_date: departureDateFromResolved,
          actual_departure_time: departure.formatted,
          estimated_arrival_time: arrival.formatted,
        },
      );
    }

    // Now due to arrive earlier than scheduled.
    if (arrivalDelay.bucket === 'early') {
      return assembleSummary(
        'FlightTrackerFlight_body_departed_arriving_early',
        strings,
        {
          ...commonBase,
          departure_date: departureDateFromResolved,
          actual_departure_time: departure.formatted,
          estimated_arrival_time: arrival.formatted,
        },
      );
    }

    return assembleSummary(
      'FlightTrackerFlight_body_departed_on_time',
      strings,
      {
        ...commonBase,
        departure_date: departureDateFromResolved,
        actual_departure_time: departure.formatted,
        scheduled_arrival_time: arrival.formatted,
      },
    );
  }

  // Not departed.
  if (flight.status === 'FLIGHT_STATUS_DELAYED') {
    // Gated on source, not `departureDelay.bucket`, so an early/on-time
    // estimate falls through to not_departed_on_time instead of here.
    if (
      departure.source === 'scheduled' ||
      departure.source === 'unavailable'
    ) {
      return assembleSummary(
        'FlightTrackerFlight_body_not_departed_no_estimate',
        strings,
        {
          ...commonBase,
          departure_date: departureDateFromSchedule,
          scheduled_departure_time: scheduledDepartureOnly.formatted,
        },
      );
    }

    if (departureDelay.bucket === 'delayed') {
      return assembleSummary(
        'FlightTrackerFlight_body_not_departed_delayed',
        strings,
        {
          ...commonBase,
          departure_date: departureDateFromSchedule,
          scheduled_departure_time: scheduledDepartureOnly.formatted,
          scheduled_arrival_time: scheduledArrivalOnly.formatted,
          time_delayed: formatMinuteDelay(
            departureDelay.minutes as number,
            locale,
            strings,
          ),
        },
      );
    }
  }

  return assembleSummary(
    'FlightTrackerFlight_body_not_departed_on_time',
    strings,
    {
      ...commonBase,
      departure_date: departureDateFromSchedule,
      scheduled_departure_time: scheduledDepartureOnly.formatted,
      scheduled_arrival_time: scheduledArrivalOnly.formatted,
    },
  );
};

const getCodesharesMessage = (
  flight: FlightStatusUpdate | null,
  strings: FlightStatusDetailsProps['strings'],
) => {
  if (!flight?.codeshares || flight.codeshares.length === 0) {
    return null;
  }

  const buildFlightCode = (airlineId: string, flightNumber: string) =>
    flightNumber.startsWith(airlineId)
      ? flightNumber
      : `${airlineId}${flightNumber}`;

  // Use the singular string for exactly one codeshare partner.
  if (flight.codeshares.length === 1) {
    const [codeshare] = flight.codeshares;
    const airlineId = codeshare.airlineId || '';
    const flightNumber = codeshare.flightNumber || '';
    const airlineName = codeshare.airlineName || airlineId;

    return reactReplacePlaceholder(
      strings.FlightTrackerFlight_codeshare_banner_single,
      {
        airline: airlineName,
        flight_number: buildFlightCode(airlineId, flightNumber),
      },
      '@@',
      '@@',
    );
  }

  const formattedCodeshares = [...flight.codeshares]
    .sort((a, b) =>
      (a.airlineName || '').localeCompare(b.airlineName || '', undefined, {
        sensitivity: 'base',
      }),
    )
    .map((codeshare) => {
      const airlineId = codeshare.airlineId || '';
      const flightNumber = codeshare.flightNumber || '';
      const airlineName = codeshare.airlineName || airlineId;
      return `${airlineName} ${buildFlightCode(airlineId, flightNumber)}`.trim();
    })
    .join(', ');

  return reactReplacePlaceholder(
    strings.FlightTrackerFlight_codeshare_banner_multiple,
    {
      codeshare_list: formattedCodeshares,
    },
    '@@',
    '@@',
  );
};

const formatLocalTime = (date: Date, locale: string): string =>
  new Intl.DateTimeFormat(locale, {
    hour: '2-digit',
    minute: '2-digit',
    timeZoneName: 'short',
  }).format(date);

type FlightStatusHeaderItemProps = PropsWithChildren<{
  label: string;
  className?: string;
  isMainTitle?: boolean;
}>;

type FlightStatusHeaderItemConfig = FlightStatusHeaderItemProps & {
  key: string;
  shouldRender?: boolean;
};

const FlightStatusHeaderItem = ({
  children = null,
  className = undefined,
  isMainTitle = false,
  label,
}: FlightStatusHeaderItemProps) => (
  <div
    className={classNames(
      STYLES.FlightStatusHeader__bannerInfoBlock,
      className,
    )}
  >
    <BpkText
      textStyle={TEXT_STYLES.footnote}
      className={STYLES.FlightStatusHeader__bannerInfoLabel}
    >
      {label}
    </BpkText>
    <BpkText tagName={isMainTitle ? 'h1' : 'h2'} textStyle={TEXT_STYLES.hero6}>
      {children}
    </BpkText>
  </div>
);

const FlightStatusHeader = ({
  carrierCode,
  locale,
  selectedFlight,
  strings,
  title,
}: FlightStatusHeaderProps) => {
  const selectedFlightDelayMinutes = useMemo(
    () =>
      selectedFlight ? getDepartureDelayMinutes(selectedFlight, locale) : null,
    [selectedFlight, locale],
  );
  // Same validated actual-only resolution as getPhase, so a malformed
  // actual-departure field can't make this gate disagree with the delay
  // figure above (which now skips malformed candidates).
  const isNotDeparted =
    !selectedFlight ||
    resolveTime(selectedFlight, 'departure', locale, ['actual']).source ===
      'unavailable';
  const displayFlight = useMemo(
    () =>
      selectedFlight?.status === 'FLIGHT_STATUS_SCHEDULED' &&
      isNotDeparted &&
      selectedFlightDelayMinutes
        ? { ...selectedFlight, status: 'FLIGHT_STATUS_DELAYED' as const }
        : selectedFlight,
    [selectedFlight, selectedFlightDelayMinutes, isNotDeparted],
  );
  const summary = useMemo(
    () => getFlightSummary(displayFlight, locale, carrierCode, strings),
    [displayFlight, locale, carrierCode, strings],
  );

  const [pageLoadTime] = useState(() => new Date());
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const handleRefreshClick = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    handleRefresh();
  };

  const codesharesMessage = useMemo(
    () => getCodesharesMessage(selectedFlight, strings),
    [selectedFlight, strings],
  );
  const isCancelled = selectedFlight?.status === 'FLIGHT_STATUS_CANCELLED';
  const isDiverted = shouldUseDivertedStatus(
    displayFlight?.status,
    isDivertedFlight(selectedFlight),
  );
  const statusLabel = displayFlight
    ? getStatusLabel(displayFlight.status, strings, isDiverted)
    : strings.FlightTrackerFlight_status_not_available;

  const headerItems: FlightStatusHeaderItemConfig[] = [
    {
      key: 'flight',
      className: STYLES.FlightStatusHeader__bannerHeaderFlightTitle,
      label: strings.FlightTrackerFlight_subheading_flight_label,
      children: title,
      isMainTitle: true,
    },
    {
      key: 'route',
      label: strings.FlightTrackerFlight_subheading_route_label,
      shouldRender:
        Boolean(selectedFlight?.departureAirportCode) &&
        Boolean(
          selectedFlight && getScheduledArrivalAirportCode(selectedFlight),
        ),
      children: formatRouteCode(
        selectedFlight?.departureAirportCode,
        selectedFlight
          ? getScheduledArrivalAirportCode(selectedFlight)
          : undefined,
      ),
    },
    {
      key: 'status',
      label: strings.FlightTrackerFlight_subheading_status_label,
      children: (
        <span className={STYLES.FlightStatusHeader__statusBadge}>
          <span
            className={classNames({
              [STYLES.FlightStatusHeader__statusNoInformationText]:
                !selectedFlight,
            })}
          >
            {statusLabel}
          </span>
          {displayFlight?.status === 'FLIGHT_STATUS_DELAYED' &&
            isNotDeparted &&
            !isDiverted &&
            selectedFlightDelayMinutes && (
              <BpkText
                textStyle={TEXT_STYLES.footnote}
                className={STYLES.FlightStatusHeader__bannerStatusDelay}
              >
                {`(${formatMinuteDelay(selectedFlightDelayMinutes, locale, strings)})`}
              </BpkText>
            )}
        </span>
      ),
    },
  ];

  return (
    <section className={STYLES.FlightStatusHeader}>
      <div className={STYLES.FlightStatusHeader__bannerHeader}>
        {headerItems.map(
          ({ key, shouldRender = true, ...headerItemProps }) =>
            shouldRender && (
              <FlightStatusHeaderItem key={key} {...headerItemProps} />
            ),
        )}
      </div>

      <div className={STYLES.FlightStatusHeader__mainContent}>
        {!isCancelled && codesharesMessage && (
          <div className={STYLES.FlightStatusHeader__codesharesBanner}>
            <BpkInfoBanner message={codesharesMessage} type="info" />
          </div>
        )}

        {isCancelled && (
          <>
            <BpkInfoBanner
              message={strings.FlightTrackerFlight_error_cancelled}
              type="info"
            />

            <div className={STYLES.FlightStatusHeader__bannerSummary}>
              <BpkText tagName="p" textStyle={TEXT_STYLES.base}>
                {strings.FlightTrackerFlight_body_cancelled}
              </BpkText>
              {codesharesMessage && (
                <BpkText tagName="p" textStyle={TEXT_STYLES.base}>
                  {codesharesMessage}
                </BpkText>
              )}
            </div>
          </>
        )}

        {!selectedFlight && (
          <BpkInfoBanner
            type="error"
            message={
              <>
                <div
                  className={
                    STYLES.FlightStatusHeader__bannerStatusNoInformationText
                  }
                >
                  {strings.FlightTrackerFlight_error_no_info}
                </div>
                <BpkLink href="/flight-tracker">
                  {strings.FlightTrackerFlight_cta_track_another}
                </BpkLink>
              </>
            }
          />
        )}

        {!isCancelled && summary && (
          <div className={STYLES.FlightStatusHeader__bannerSummary}>
            <BpkText tagName="p" textStyle={TEXT_STYLES.base}>
              {summary}
            </BpkText>
          </div>
        )}
      </div>

      <div className={STYLES.FlightStatusHeader__statusRow}>
        <div className={STYLES.FlightStatusHeader__statusBadgeContainer}>
          {strings.FlightTrackerFlight_status_label}
          <FlightStatusBadge flight={displayFlight} strings={strings} />
        </div>
        <span className={STYLES.FlightStatusHeader__statusLastUpdated}>
          {isHydrated &&
            reactReplacePlaceholder(
              strings.FlightTrackerFlight_status_page_updated,
              {
                update_time: formatLocalTime(pageLoadTime, locale),
              },
              '@@',
              '@@',
            )}
          <BpkLink href="#" onClick={handleRefreshClick}>
            {strings.FlightTrackerFlight_status_refresh}
          </BpkLink>
        </span>
      </div>

      {selectedFlight && (
        <hr className={STYLES.FlightStatusHeader__sectionDivider} />
      )}
    </section>
  );
};

export default FlightStatusHeader;
